/// <amd-module name="Wit/WorkItemForm/Attachments/AttachmentThumbnails" />
import * as React from "react";
import { useEffect, useState } from "react";
import { useService } from "VSS/Platform/Layout";
import { Ago } from "VSSUI/Ago";
import { Icon } from "VSSUI/Icon";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Spinner } from "VSSUI/Spinner";
import { ILinkInfo } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemFormService } from "Wit/WorkItemForm/Common/Services";
import { useWorkItem } from "Wit/WorkItemForm/Common/Hooks";
import { getDateInUserTimeZone, tooltipTimeFormat } from "Wit/Common/DateUtils";
import { AttachmentMoreButton } from "./AttachmentMoreButton";
import { curryAttachmentActions, IAttachmentActions, ICurriedAttachmentActions } from "./AttachmentActions";
import { getAttachmentIconName, getAttachmentPreviewType } from "./AttachmentUtils";
import { useAttachmentUri } from "./useAttachmentUri";
import { IdentityReference } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { IObservableArray } from "VSS/Core/Observable";
import { Observer } from "VSSUI/Observer";

export interface IAttachmentThumbnailsProps extends IAttachmentActions {
    attachments: IObservableArray<ILinkInfo>;
}

export interface IAttachmentThumbnailProps extends ICurriedAttachmentActions {
    attachment: ILinkInfo;
}

export function AttachmentThumbnails({ attachments, ...actions }: IAttachmentThumbnailsProps) {
    return (
        <div className="flex-row flex-wrap">
            <Observer attachments={attachments}>
                {({ attachments }: { attachments: ILinkInfo[] }) =>
                    attachments.map(attachment => {
                        const curriedActions = curryAttachmentActions(attachment, actions);
                        return <AttachmentThumbnailCard key={attachment.FilePath} attachment={attachment} {...curriedActions} />;
                    })
                }
            </Observer>
        </div>
    );
}

function AttachmentThumbnailCard({ attachment, ...curriedActions }: IAttachmentThumbnailProps) {
    const [isHovered, setHovered] = useState(false);
    return (
        <div
            className="attachment-thumbnail-card"
            onDoubleClick={curriedActions.onPreviewAttachment}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <AttachmentThumbnail className="attachment-thumbnail" attachment={attachment} />
            <div className="attachment-thumbnail-details">
                <div className="attachment-thumbnail-file-name">{attachment.OriginalName}</div>
                <Attacher className="attachment-thumbnail-attacher" attachment={attachment} />
            </div>
            {isHovered && <AttachmentMoreButton {...curriedActions} />}
        </div>
    );
}

function AttachmentThumbnail({ className, attachment }: { className: string; attachment: ILinkInfo }) {
    const previewType = getAttachmentPreviewType(attachment.OriginalName ?? "");
    const src = useAttachmentUri(attachment);

    return (
        <div className={className}>
            {!attachment.FilePath ? (
                <Spinner />
            ) : previewType === "Image" ? (
                <img className="attachment-thumbnail-image" src={src} />
            ) : (
                <Icon className="attachment-thumbnail-icon font-size-xxxl" iconName={getAttachmentIconName(attachment.OriginalName ?? "")} />
            )}
        </div>
    );
}

function Attacher({ className, attachment }: { className: string; attachment: ILinkInfo }) {
    const [attacher, setAttacher] = useState<IdentityReference | undefined>(undefined);
    const [attachedDate, setAttachedDate] = useState<Date | undefined>(undefined);

    const workItem = useWorkItem();
    const workItemFormService = useService<IWorkItemFormService>("IWorkItemFormService");

    useEffect(() => {
        workItemFormService.getHistoryData(workItem.id, workItem.project.guid).then(historyItems => {
            for (const historyItem of historyItems) {
                for (const relation of historyItem.relations?.added ?? []) {
                    if (relation.attributes.name === attachment.OriginalName) {
                        setAttacher(historyItem.revisedBy);
                        setAttachedDate(historyItem.revisedDate);
                        return;
                    }
                }
            }
        });
    }, [attachment.OriginalName]);

    return (
        <div className={`${className} flex-row`}>
            {attacher ? <Persona identity={attacher} size={PersonaSize.size32} /> : <PersonaPlaceholder size={PersonaSize.size32} />}

            <div className="attacher-label flex-grow font-size">
                <div className="attacher-name font-weight-semibold">{attacher ? attacher.displayName : <TextPlaceholder width={130} />}</div>

                <div className="attach-date">{attachment.AddedDate ? <Ago date={getDateInUserTimeZone(attachment.AddedDate)} currentDate={getDateInUserTimeZone()} tooltipTimeFormat={tooltipTimeFormat} /> : <TextPlaceholder width={80} />}</div>
            </div>
        </div>
    );
}

function PersonaPlaceholder({ size }: { size: PersonaSize }) {
    return <div className="shimmer" style={{ width: size, height: size, borderRadius: size / 2 }} />;
}

function TextPlaceholder({ width }: { width: number }) {
    return <div className="shimmer shimmer-line margin-bottom-4" style={{ height: 12, width }} />;
}
